import { Link2, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";
import { DeleteItemModal } from "@/features/brands/edit/components/shared/DeleteItemModal";

import { CopyEmailButton } from "./CopyEmailButton";
import { getLinkMeta, getShareTarget } from "./link-meta";

type AssetLinkRowProps = {
  onDelete?: (url: string) => void;
  showShareHint: boolean;
  url: string;
};

export function AssetLinkRow({
  onDelete,
  showShareHint,
  url,
}: AssetLinkRowProps) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { label, shortUrl } = getLinkMeta(url);
  const shareTarget = showShareHint ? getShareTarget(url) : null;

  return (
    <li className="flex min-w-0 animate-in items-center gap-lg rounded-xl border border-border-secondary bg-bg-primary py-md pl-lg pr-md duration-200 fade-in zoom-in-95 motion-reduce:animate-none">
      <span className="grid size-9 shrink-0 place-items-center rounded-[9px] border border-border-secondary bg-bg-secondary text-text-quaternary">
        <Link2 className="size-4" />
      </span>
      <div className="min-w-0 flex-1 leading-[1.35]">
        <a
          className="block w-fit max-w-full truncate rounded-xs text-[13px] font-semibold text-text-primary outline-hidden hover:underline focus-visible:ring-2 focus-visible:ring-fg-brand-primary"
          href={url}
          rel="noopener noreferrer"
          target="_blank"
          title={url}
        >
          {label}
        </a>
        <span className="block truncate text-xs text-text-quaternary">
          {shortUrl}
        </span>
        {shareTarget ? (
          <span className="mt-xxs block text-xs text-text-quaternary">
            Make sure{" "}
            <b className="font-semibold text-text-secondary">
              {shareTarget.email}
            </b>{" "}
            has access. <CopyEmailButton {...shareTarget} />
          </span>
        ) : null}
      </div>
      {onDelete ? (
        <>
          <Button
            aria-label={`Remove link ${label}`}
            className="size-7 shrink-0 cursor-pointer rounded-[7px] text-text-quaternary outline-hidden hover:bg-bg-secondary hover:text-text-primary focus-visible:ring-2 focus-visible:ring-fg-brand-primary"
            onPress={() => setIsDeleteOpen(true)}
            variant="unstyled"
          >
            <X className="size-3.5" />
          </Button>
          <DeleteItemModal
            bodyText="This link will be removed from your brief."
            isOpen={isDeleteOpen}
            itemLabel="link"
            onConfirm={() => {
              onDelete(url);
              setIsDeleteOpen(false);
            }}
            onOpenChange={setIsDeleteOpen}
          />
        </>
      ) : null}
    </li>
  );
}
